import { useCallback } from 'react'
import type {
  UpdatePdfStateFunction,
  GenerationState,
} from '@/hooks/pdf/usePdfViewer'

type UsePdfSettersParams = {
  updatePdfState: UpdatePdfStateFunction
}

export const usePdfSetters = ({ updatePdfState }: UsePdfSettersParams) => {
  const setShowPdfViewer = useCallback(
    (value: boolean) => updatePdfState({ showPdfViewer: value }),
    [updatePdfState],
  )

  const setDisclaimerAcknowledged = useCallback(
    (value: boolean) => updatePdfState({ disclaimerAcknowledged: value }),
    [updatePdfState],
  )

  const setPendingCloseOperation = useCallback(
    (value: boolean) => updatePdfState({ pendingCloseOperation: value }),
    [updatePdfState],
  )

  const setShouldSetProductStatusToFinalized = useCallback(
    (value: boolean) =>
      updatePdfState({ shouldSetProductStatusToFinalized: value }),
    [updatePdfState],
  )

  const setShouldSetProductStatusToEditing = useCallback(
    (value: boolean) =>
      updatePdfState({ shouldSetProductStatusToEditing: value }),
    [updatePdfState],
  )

  const setShouldSetProductStatusToProofSent = useCallback(
    (value: boolean) =>
      updatePdfState({ shouldSetProductStatusToProofSent: value }),
    [updatePdfState],
  )

  const setGeneratingText = useCallback(
    (value: string) => updatePdfState({ generatingText: value }),
    [updatePdfState],
  )

  const setDisableCustomizeProductHeaderDropdowns = useCallback(
    (value: boolean) =>
      updatePdfState({ previewCustomizeProductHeaderDropdownsDisabled: value }),
    [updatePdfState],
  )

  const setGenerating = useCallback(
    (value: boolean) => updatePdfState({ generating: value }),
    [updatePdfState],
  )

  const setCanCustomize = useCallback(
    (value: boolean) => updatePdfState({ canCustomize: value }),
    [updatePdfState],
  )

  const setCanRevertFinalization = useCallback(
    (value: boolean) => updatePdfState({ canRevertFinalization: value }),
    [updatePdfState],
  )

  const setIsProof = useCallback(
    (value: boolean) => updatePdfState({ isProof: value }),
    [updatePdfState],
  )

  const setGenerationState = useCallback(
    (value: GenerationState) => updatePdfState({ generationState: value }),
    [updatePdfState],
  )

  const setProofGalleryDetails = useCallback(
    (
      value: {
        pdfId: string
        galleryToken: string | null
        screenshotUrls: string[]
      } | null,
    ) => updatePdfState({ proofGalleryDetails: value }),
    [updatePdfState],
  )

  return {
    setShowPdfViewer,
    setDisclaimerAcknowledged,
    setPendingCloseOperation,
    setShouldSetProductStatusToFinalized,
    setShouldSetProductStatusToEditing,
    setShouldSetProductStatusToProofSent,
    setGeneratingText,
    setDisableCustomizeProductHeaderDropdowns,
    setGenerating,
    setCanCustomize,
    setCanRevertFinalization,
    setIsProof,
    setGenerationState,
    setProofGalleryDetails,
  }
}
